import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toggleModal, modalState, detailOpenState } from 'reducers/ui';

import { useDispatch, useSelector } from 'react-redux';

import staticText from 'static/text_display.json';
import useFilters from 'containers/Base/useFilters.js';

import { getUserAssignedDepartmentList } from 'reducers/users';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import { actions as zoneActions } from '../Modules/dataGridTable';
import { actions as zoneFilterActions } from '../Modules/filters';
import { nameSpace } from '../Modules/nameSpace';
import { filters } from '../filters';
import * as zoneSelectors from '../Selectors/zoneSelectors';
import { PAGE_URLS } from 'utils/constants';

const Zone = ({
  editable,
  filtersRequired,
  columns,
  // map,
  details,
  FormComponent,
}) => {
  //-------------------------hooks---------------------------------
  const [ZoneGridApi, setZoneGridApi] = useState();
  //---------------------------Selectors---------------------------
  const onMapSelectedZoneId = useSelector(state =>
    zoneSelectors.getSelectedRowID(state),
  );
  const loading = useSelector(state => state.ui.loading);
  const detailOpen = useSelector(state => detailOpenState(state));
  const zoneListData = useSelector(state =>
    zoneSelectors.getZoneListTransformed(state),
  );
  const modalOpen = useSelector(state => modalState(state));
  const filterValues = useSelector(state =>
    zoneSelectors.getZoneFilters(state),
  );
  const totalEntries = useSelector(state =>
    zoneSelectors.getTotalRowsCount(state),
  );
  const forceRefresh = useSelector(state =>
    zoneSelectors.getForceRefresh(state),
  );

  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  //--------------------------customHooks--------------------------
  const { localFilterValues, updateFilter } = useFilters({
    filterValues,
  });
  //---------------------------Actions------------------------------
  const dispatch = useDispatch();

  const dispatchToggleModal = useCallback(
    () => dispatch(toggleModal(nameSpace)),
    [dispatch],
  );

  const dispatchFetchZoneList = useCallback(
    ({ filters, from }) =>
      dispatch(zoneActions.fetchGridList({ filters, from })),
    [dispatch],
  );

  //------------------------------------------------------------------------------
  const onUpdate = filters => {
    dispatchFetchZoneList({
      filters,
    });
  };
  const fetchZones = from => {
    dispatchFetchZoneList({
      filters: localFilterValues,
      from,
    });
  };

  const gridAPIHandler = gridAPI => {
    setZoneGridApi(gridAPI);
  };

  useEffect(() => {
    const zoneHighLight = onMapSelectedZoneId => {
      if (onMapSelectedZoneId !== null) {
        const node = ZoneGridApi && ZoneGridApi.getRowNode(onMapSelectedZoneId);
        // Go to the highlighted node
        ZoneGridApi && ZoneGridApi.ensureNodeVisible(node, { position: 'top' });
        if (node) {
          if (!node.isSelected()) {
            node.setSelected(true);
          }
        }
      }
    };
    zoneHighLight(onMapSelectedZoneId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMapSelectedZoneId, ZoneGridApi]);

  // memoize the form component
  const ZoneFormComponent = useCallback(
    props => (
      <FormComponent
        {...props}
        userAssignedDepartments={userAssignedDepartments}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <LayoutWithMap
      header="Zones"
      cols={columns}
      data={zoneListData}
      DetailComponent={details}
      // for creation / edit
      toggleModal={dispatchToggleModal}
      onEdit={modalOpen[nameSpace]}
      editable={editable}
      filtersRequired={filtersRequired}
      filterValues={localFilterValues}
      filters={filters(userAssignedDepartments)}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: filters => {
            dispatch(zoneFilterActions.setFilterKey(true));
            dispatch(zoneFilterActions.setFilters(filters));
          },
        })
      }
      FormComponent={ZoneFormComponent}
      onUpdate={onUpdate}
      fetchData={fetchZones}
      rowModelType="infinite"
      forceRefresh={forceRefresh}
      totalEntries={totalEntries}
      createTextHover={staticText.managerial_view.zone.create}
      setGridAPI={gridAPIHandler}
      persistentStateName={PAGE_URLS.ZONES.name}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.ZONES.link]}
      loading={loading}
    />
  );
};

Zone.propTypes = {
  editable: PropTypes.bool,
  filtersRequired: PropTypes.bool,
  columns: PropTypes.array,
  map: PropTypes.object,
  details: PropTypes.object,
  FormComponent: PropTypes.elementType,
};

Zone.defaultProps = {
  editable: true,
  filtersRequired: true,
};

export default Zone;
